import React from 'react';
import Page from '../../../../components/Page'; // Assuming this is a general layout component
import { useTranslation } from 'react-i18next';
import { Button } from "src/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "src/components/ui/card";
import { Mail, UploadCloud } from 'lucide-react'; // Icons for cards
import stripeLogo from './stripe.webp';
import shopifyLogo from './shopify.png';
// --- Placeholder SVG Logo Components ---
// TODO: Replace these placeholders with your actual SVG imports.
// Example: import StripeLogoSrc from 'src/assets/icons/stripe-logo.svg?react';
// Example: import ShopifyLogoSrc from 'src/assets/icons/shopify-logo.svg?react';

// Placeholder for Stripe Logo (PNG)
// TODO: Ensure 'stripe-logo.png' is in your public assets folder (e.g., /public/assets/logos/stripe-logo.png)
// or import it if it's in the src directory.
const StripeLogo: React.FC<{ className?: string }> = ({ className }) => (
  <img
    src={stripeLogo} // Adjust path as needed
    alt="Stripe Logo"
    className='p-5 pt-7'
  // className={className} // This will apply h-16 w-16 from the CardHeader
  />
);
const ShopifyLogo: React.FC<{ className?: string }> = ({ className }) => (
  <img
    src={shopifyLogo} // Adjust path as needed
    alt="Stripe Logo"
  // className={className} // This will apply h-16 w-16 from the CardHeader
  />
);


// Define a type for automation templates
interface AutomationTemplate {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType | React.FC<{ className?: string }>; // Lucide icon or custom component
  actionText: string;
  actionType: 'configure' | 'email' | 'upload';
  emailDetails?: {
    to: string;
    subject: string;
  };
}

const automationTemplates: AutomationTemplate[] = [
  // Stripe Templates
  {
    id: 'stripe-new-purchase',
    title: 'After New Purchase',
    description: 'Send a review invitation immediately after a customer completes a purchase.',
    icon: StripeLogo, // Use StripeLogo component
    actionText: 'We are working on this',
    actionType: 'configure',
  },
  {
    id: 'stripe-subscription-renewal',
    title: 'X Days After Subscription',
    description: 'Send a review invitation X days after a new subscription starts or an existing one renews.',
    icon: StripeLogo, // Use StripeLogo component
    actionText: 'We are working on this',
    actionType: 'configure',
  },
  // Shopify Templates
  {
    id: 'shopify-order-fulfilled',
    title: 'After Order Fulfillment',
    description: 'Send a review invitation as soon as an order is marked as fulfilled in Shopify.',
    icon: ShopifyLogo, // Use ShopifyLogo component
    actionText: 'We are working on this',
    actionType: 'configure',
  },
  {
    id: 'shopify-x-days-after-fulfillment',
    title: 'X Days After Fulfillment',
    description: 'Send a review invitation X days after an order has been fulfilled.',
    icon: ShopifyLogo, // Use ShopifyLogo component
    actionText: 'We are working on this',
    actionType: 'configure',
  },
  {
    id: 'request-integration',
    title: 'Request an automation',
    description: "Can't find your platform? Let us know and we'll look into it.",
    icon: Mail,
    actionText: 'Request an automation',
    actionType: 'email',
    emailDetails: {
      to: 'lasse@simplyreview.com', // Placeholder email
      subject: 'New Automation Request',
    },
  },
];

export default function CreateScreen() {
  const { t } = useTranslation();

  const handleCardAction = (template: AutomationTemplate) => {
    if (template.actionType === 'email' && template.emailDetails) {
      window.location.href = `mailto:${template.emailDetails.to}?subject=${encodeURIComponent(template.emailDetails.subject)}`;
    } else {
      // For 'configure' or 'upload', you might navigate to a different screen or open a modal
      console.log(`Action for ${template.title}: ${template.actionText}`);
      // Example: navigate(`/automations/configure/${template.id}`);
    }
  };

  return (
    <Page title={t('reviewAutomation.create.title', 'Create New Review Automation')}>
      <div className="container mx-auto p-4 md:p-6 lg:p-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight">
            {t('reviewAutomation.create.headline', 'Choose an Automation Template')}
          </h1>
          <p className="text-muted-foreground mt-2">
            {t('reviewAutomation.create.subheadline', 'Select a pre-built template to get started quickly or request a new one.')}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {automationTemplates.map((template) => (
            <Card key={template.id} className="flex flex-col">
              <CardHeader className="pb-0 justify-between h-full">
                <div className="flex items-center justify-center">
                  <template.icon className="h-16 w-16" />
                </div>
                <div>
                  <CardTitle className="text-xl">{template.title}</CardTitle>
                  <CardDescription className="text-sm min-h-[40px]">{template.description}</CardDescription>
                </div>

              </CardHeader>
              <CardContent className="flex-grow">
                {/* Additional content can go here if needed */}
              </CardContent>
              <CardFooter>
                <Button
                  disabled={template.actionType !== 'email'}
                  className="w-full"
                  onClick={() => handleCardAction(template)}
                  variant={template.actionType === 'email' ? 'outline' : 'default'}
                >
                  {template.actionText}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      </div>
    </Page>
  );
}
